import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import CodingalProductBlog from '@components/blog/product-blogs'
import { useState } from 'react'
import { formatSeoConfig } from '@lib/seo'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import ContentCard from '@components/course-landing-page/content-card'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { TEACHABLE_MACHINE } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['aigc'].trialCourseQuery}`

export const TeachableMachineSeo = formatSeoConfig({
  title: 'Teachable Machine for Kids: Build AI Projects',
  description:
    'Teach kids AI with expert-led Teachable Machine classes. Build and train real models, explore machine learning hands-on at Codingal. Try a free lesson.',
  url: 'https://www.codingal.com/courses/teachable-machine/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-dark-v1.jpg',
})
const TeachableMachine = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...TeachableMachineSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[TEACHABLE_MACHINE].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Learn Teachable Machine with the Top AI & coding Course Instructors for Kids and Teens"
        description="At Codingal, students in Grades 3–8 learn to build and train real AI models using Google’s Teachable Machine, a fun and intuitive platform for teaching computers to recognize images, sounds, and poses. Integrated across multiple AI and Scratch-based courses, students use these trained models to create interactive games, smart characters, and real-world simulations in Scratch using the MIT Raise Playground. Every lesson encourages hands-on learning, creative problem-solving, and a deeper understanding of how AI works in everyday life."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={TEACHABLE_MACHINE}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={TEACHABLE_MACHINE} />
          <CourseFeatures
            courseTitle={TEACHABLE_MACHINE}
            title={
              <Trans>
                An engaging Teachable Machine programming lessons for kids
              </Trans>
            }
          />
          <ClassCurriculum
            courseName={TEACHABLE_MACHINE}
            title="Our Teachable Machine Class Curriculum"
            description={
              <>
                <Trans>
                  At Codingal, students in Grades 3–8 dive into Artificial
                  Intelligence by training real machine learning models using
                  Google’s Teachable Machine and integrating them into
                  interactive projects with Scratch via MIT Raise Playground.
                  From guiding spy agents with voice commands and recognizing
                  countries through gestures to building fruit recognition games
                  and applying face filters, students create smart, responsive
                  characters using AI. They explore concepts like image and
                  sound classification, face sensing, supervised learning, and
                  emotional response modeling, all in a playful, hands-on
                  format. Every activity reinforces creative thinking,
                  problem-solving, and real-world AI applications, laying a
                  strong foundation for future tech learning.
                </Trans>
              </>
            }
          />
          <Activities courseName={TEACHABLE_MACHINE} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked AI & coding
                tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                <p>
                  Our instructors bring deep expertise in Computer Science and
                  complete rigorous training before they teach; reflected in our
                  exceptional Net Promoter Score (NPS) of 86. Students and
                  parents consistently rate our teachers 4.9 out of 5, and our
                  curriculum earns a 4.8 out of 5. We’re proud to have a faculty
                  that’s 91% female and 9% male, creating an inclusive,
                  encouraging learning environment. Each tutor is trained to
                  understand individual student needs and deliver personalized
                  support; even in group settings. Plus, students have 24/7
                  access to expert tutors for instant doubt resolution, anytime
                  they need help.
                </p>
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the fundamentals of AI with
                Teachable Machine
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  At Codingal, we create the ideal environment for kids to
                  explore Artificial Intelligence with Teachable Machine and
                  Scratch. Our curriculum blends real-world AI tools, creative
                  projects, and interactive learning, making complex concepts
                  simple, engaging, and fun for young minds.
                </span>
              </Trans>
            }
            courseName={TEACHABLE_MACHINE}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                <p>
                  At Codingal, your child explores Artificial Intelligence
                  through a STEM.org–accredited program designed around trusted
                  educational frameworks like Bloom’s Taxonomy, BIDE, and STEAM.
                  Our curriculum aligns with the K–12 Computer Science Framework
                  and supports key skills in logic, creativity, and
                  problem-solving. Through interactive, project-based learning,
                  students train real AI models using Teachable Machine and
                  build engaging experiences in Scratch! From gesture-based
                  games to image recognition tools. Every project blends
                  computer science with real-world thinking, helping kids apply
                  AI in fun, meaningful ways. Backed by STEM.org’s rigorous
                  standards, this course builds a strong, future-ready
                  foundation in STEM and rewards learners with a recognized
                  Certificate of Completion.
                </p>
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your AI Teachable Machine certifications"
            description={
              <>
                In Codingal’s AI Creativity track with Teachable Machine and
                Scratch, students in Grades 3–8 train real machine learning
                models and apply them in interactive projects using Scratch and
                MIT Raise Playground. They progress through modules covering
                image recognition, sound classification, gesture detection, and
                face tracking; learning how AI understands and responds to the
                world. Upon successful completion, students earn the
                STEM.org–certified AI Creativity Certificate; a valuable
                credential for academic portfolios, presentations, and future
                learning opportunities. With practical AI experience and a
                recognized certificate, learners build real-world skills,
                creative confidence, and a strong foundation for tomorrow’s
                tech-driven careers.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our Teachable Machine classes are appropriate for middle school & high school students"
            description={
              <>
                Our AI + Scratch classes are perfect for elementary and middle
                school students. At Codingal, our Teachable Machine + Scratch
                curriculum introduces students in Grades 3–8 to the exciting
                world of Artificial Intelligence through age-appropriate,
                hands-on learning. We start with simple activities like image
                and sound recognition using Teachable Machine, then integrate
                those models into Scratch to build interactive games and smart
                characters. As students advance, they explore real AI concepts
                like classification, gesture control, and face tracking;
                developing logical thinking, creativity, and tech fluency along
                the way. Each project builds confidence and curiosity,
                empowering students to understand and apply AI in the world
                around them.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['aigc', 'cgm', 'cp']} />
          <Faq
            courseName={TEACHABLE_MACHINE}
            title="Frequently Asked Questions (FAQs) – Teachable Machine for Kids"
          />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            title="Latest from Our Blog"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={TEACHABLE_MACHINE}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Teachable Machine for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default TeachableMachine
